import { ArrowDown, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import React from "react";
import { Link } from "wouter";
import { QuoteBand } from "@/components/QuoteBand";
import { StructuredData } from "@/components/StructuredData";
import { mountingStyles, type MountingStyle } from "@/data/resources";
import { siteConfig } from "@/data/site";

function MountingProductImage({ style, image = style.image, sizes = "(max-width: 820px) 100vw, 44vw" }: { style: MountingStyle; image?: MountingStyle["image"]; sizes?: string }) {
  return (
    <div className={`mounting-product-image mounting-product-image--${style.slug}`}>
      <img
        src={image.src}
        srcSet={`${image.smallSrc} 720w, ${image.src} 1280w`}
        sizes={sizes}
        width={image.width}
        height={image.height}
        alt={image.alt}
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

function MountingTechnicalDiagrams({ style }: { style: MountingStyle }) {
  const hasFloorSupport = style.slug !== "ceiling-hung";
  const hasCeilingSupport = style.slug === "ceiling-hung" || style.slug === "floor-to-ceiling";
  const hasHeadrail = style.slug === "overhead-braced";

  return (
    <div className="mounting-showcase__diagrams" aria-label={`Illustrative ${style.name} planning views`}>
      <figure className="mounting-diagram">
        <div className="mounting-diagram__stage">
          <svg viewBox="0 0 160 118" aria-hidden="true">
            <rect className="mounting-diagram__wall" x="14" y="13" width="8" height="88" />
            <rect className="mounting-diagram__wall" x="138" y="13" width="8" height="88" />
            {hasCeilingSupport && <line className="mounting-diagram__ceiling" x1="14" y1="15" x2="146" y2="15" />}
            <line className="mounting-diagram__floor" x1="14" y1="101" x2="146" y2="101" />
            <rect className="mounting-diagram__panel" x="37" y="42" width="33" height="49" />
            <rect className="mounting-diagram__panel" x="75" y="42" width="33" height="49" />
            <rect className="mounting-diagram__panel" x="113" y="42" width="17" height="49" />
            {hasHeadrail && <rect className="mounting-diagram__headrail" x="26" y="31" width="108" height="6" />}
            {hasCeilingSupport && <><line className="mounting-diagram__support" x1="35" y1="16" x2="35" y2="41" /><line className="mounting-diagram__support" x1="74" y1="16" x2="74" y2="41" /><line className="mounting-diagram__support" x1="111" y1="16" x2="111" y2="41" /></>}
            {hasFloorSupport && <><line className="mounting-diagram__support" x1="35" y1="91" x2="35" y2="100" /><line className="mounting-diagram__support" x1="74" y1="91" x2="74" y2="100" /><line className="mounting-diagram__support" x1="111" y1="91" x2="111" y2="100" /></>}
          </svg>
        </div>
        <figcaption>Front elevation</figcaption>
      </figure>
      <figure className="mounting-diagram">
        <div className="mounting-diagram__stage">
          <svg viewBox="0 0 160 118" aria-hidden="true">
            <rect className="mounting-diagram__wall" x="14" y="14" width="10" height="90" />
            <line className="mounting-diagram__floor" x1="24" y1="103" x2="146" y2="103" />
            <line className="mounting-diagram__line" x1="24" y1="24" x2="145" y2="24" />
            <line className="mounting-diagram__line" x1="24" y1="62" x2="127" y2="62" />
            <path className="mounting-diagram__door" d="M127 24v38m0 0A38 38 0 0 0 89 24" />
            <path className="mounting-diagram__door" d="M127 62v41m0 0a41 41 0 0 0-41-41" />
            {hasFloorSupport && <><circle className="mounting-diagram__anchor" cx="31" cy="24" r="2.5" /><circle className="mounting-diagram__anchor" cx="31" cy="62" r="2.5" /><circle className="mounting-diagram__anchor" cx="31" cy="103" r="2.5" /></>}
          </svg>
        </div>
        <figcaption>Typical floor plan</figcaption>
      </figure>
      <figure className="mounting-diagram">
        <div className="mounting-diagram__stage">
          <svg viewBox="0 0 160 118" aria-hidden="true">
            <rect className="mounting-diagram__wall" x="14" y="13" width="9" height="88" />
            {hasCeilingSupport && <line className="mounting-diagram__ceiling" x1="14" y1="15" x2="146" y2="15" />}
            <line className="mounting-diagram__floor" x1="14" y1="101" x2="146" y2="101" />
            <rect className="mounting-diagram__panel" x="49" y="42" width="10" height="49" />
            <line className="mounting-diagram__line" x1="23" y1="42" x2="59" y2="42" />
            {hasHeadrail && <rect className="mounting-diagram__headrail" x="23" y="31" width="56" height="6" />}
            {hasCeilingSupport && <line className="mounting-diagram__support" x1="54" y1="16" x2="54" y2="41" />}
            {hasFloorSupport && <line className="mounting-diagram__support" x1="54" y1="91" x2="54" y2="100" />}
          </svg>
        </div>
        <figcaption>Side elevation</figcaption>
      </figure>
    </div>
  );
}

function MountingStylePicker() {
  return (
    <nav className="mounting-style-picker" aria-label="Jump to a mounting-style detail">
      {mountingStyles.map((style) => (
        <a href={`#${style.slug}`} className="mounting-style-picker__item" key={style.slug}>
          <MountingProductImage style={style} image={style.selectorImage} sizes="(max-width: 560px) 100vw, (max-width: 1120px) 50vw, 25vw" />
          <span className="mounting-style-picker__index">{style.number}</span>
          <strong>{style.name}</strong>
          <span className="mounting-style-picker__action">Jump to details <ArrowDown aria-hidden="true" /></span>
        </a>
      ))}
    </nav>
  );
}

export default function MountingStyles() {
  const url = `${siteConfig.url}/resources/mounting-styles`;
  const openingVisual = mountingStyles[0].image;

  return (
    <>
      <StructuredData data={{ "@context": "https://schema.org", "@graph": [
        { "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`, itemListElement: [{ "@type": "ListItem", position: 1, name: "Home", item: `${siteConfig.url}/` }, { "@type": "ListItem", position: 2, name: "Bathroom Partitions", item: `${siteConfig.url}/bathroom-partitions` }, { "@type": "ListItem", position: 3, name: "Mounting Options", item: url }] },
        { "@type": "WebPage", "@id": `${url}#webpage`, url, name: "Commercial Bathroom Partition Mounting Options", description: "Compare Overhead-Braced, Floor-Mounted, Ceiling-Hung, and Floor-to-Ceiling bathroom partition systems.", isPartOf: { "@id": `${siteConfig.url}/#website` }, mainEntity: { "@id": `${url}#article` }, breadcrumb: { "@id": `${url}#breadcrumb` }, primaryImageOfPage: `${siteConfig.url}${openingVisual.src}` },
        { "@type": "Article", "@id": `${url}#article`, headline: "Commercial Bathroom Partition Mounting Options", description: "Compare Overhead-Braced, Floor-Mounted, Ceiling-Hung, and Floor-to-Ceiling bathroom partition systems.", author: { "@id": `${siteConfig.url}/#business` }, publisher: { "@id": `${siteConfig.url}/#business` }, mainEntityOfPage: { "@id": `${url}#webpage` }, image: `${siteConfig.url}${openingVisual.src}` },
      ] }} />

      <header className="simple-page-hero simple-page-hero--guide mounting-choice-hero section-line">
        <div className="simple-page-hero__copy">
          <p className="breadcrumb"><Link href="/">Home</Link><span>/</span><Link href="/bathroom-partitions">Bathroom partitions</Link><span>/</span>Mounting styles</p>
          <span className="kicker">Four mounting styles</span>
          <h1>Choose how the partitions are supported.</h1>
          <p>Mounting affects appearance, cleaning access, structure, and installation. Start with the room conditions, then compare the four common options below.</p>
          <div className="button-row"><a href="#mounting-options" className="button button--primary">Compare Options <ArrowDown aria-hidden="true" /></a><Link href="/request-a-quote" className="button button--outline">Discuss Your Layout <ArrowUpRight aria-hidden="true" /></Link></div>
        </div>
        <figure>
          <img src={openingVisual.src} srcSet={`${openingVisual.smallSrc} 720w, ${openingVisual.src} 1280w`} sizes="(max-width: 820px) 100vw, 52vw" width={openingVisual.width} height={openingVisual.height} alt={openingVisual.alt} fetchPriority="high" />
          <figcaption>Compare headrail, floor, and overhead support before pricing the layout.</figcaption>
        </figure>
      </header>

      <section id="mounting-options" className="mounting-choice-section section-line" aria-labelledby="mounting-picker-title">
        <header className="mounting-choice-section__heading">
          <div><span className="kicker">Choose a support system</span><h2 id="mounting-picker-title">See the support system, then jump straight to its details.</h2></div>
          <p>Tap an image to go directly to the support conditions, planning views, and quote questions for that mounting style.</p>
        </header>
        <MountingStylePicker />
      </section>

      <section className="mounting-detail-section section-line" aria-labelledby="mounting-detail-title">
        <header className="mounting-detail-section__heading">
          <div><span className="kicker">Mounting details</span><h2 id="mounting-detail-title">What to confirm for each style.</h2></div>
          <p>These views are planning aids, not final shop drawings. Field conditions and manufacturer requirements determine the final system.</p>
        </header>
        <div className="mounting-showcase">
          {mountingStyles.map((style) => (
            <article id={style.slug} className="mounting-showcase__item" key={style.slug}>
              <div className="mounting-showcase__brief">
                <div className="mounting-showcase__heading"><span>{style.number} / {style.shortLabel}</span><h2>{style.name}</h2><p>{style.overview}</p></div>
                <div className="mounting-showcase__facts" aria-label={`${style.name} selection details`}><div><strong>Best for</strong><p>{style.bestFor}</p></div><div><strong>Main advantage</strong><p>{style.mainAdvantage}</p></div><div><strong>Confirm first</strong><p>{style.confirmFirst}</p></div></div>
                <MountingTechnicalDiagrams style={style} />
                <details className="mounting-showcase__checklist"><summary>Planning checklist</summary><ul>{style.considerations.map((item) => <li key={item}><Check aria-hidden="true" />{item}</li>)}</ul><p><strong>Common material availability</strong>{style.compatibleMaterials.join(" · ")}</p></details>
              </div>
              <figure className="mounting-showcase__photo"><MountingProductImage style={style} /><figcaption>Illustrative {style.name.toLowerCase()} configuration. Final layout varies by project conditions and selected system.</figcaption><Link href="/request-a-quote" className="text-link">Discuss this option <ArrowRight aria-hidden="true" /></Link></figure>
            </article>
          ))}
        </div>
      </section>

      <section className="next-guide section-line"><div><span className="kicker">Next planning step</span><h2>Document the room accurately.</h2><p>Once the likely support system is understood, collect the dimensions and finished conditions that shape the layout.</p></div><Link href="/resources/how-to-measure">Open the measuring guide <ArrowRight aria-hidden="true" /></Link></section>
      <QuoteBand title="Not sure which mounting option fits the room?" copy="Send plans, measurements, photos, and any floor or ceiling information you have. We will help organize the right questions before pricing." />
    </>
  );
}
